/**
 * Shared HTTP 202 ("result not ready yet") polling for connector downloads.
 *
 * A fetch that throws {@link RetryNeededError} is retried on a fixed
 * schedule until the 10-minute budget expires ({@link RetryTimeoutError}).
 * The budget counts only the waits between polls, so a slow link cannot
 * shorten the number of polls; bounding a request or a batch is the
 * caller's own job.
 */

export class RetryNeededError extends Error {
    /** Legacy discriminator kept for `connector-fetcher` consumers. */
    public readonly status = 'retry_needed' as const;

    public constructor() {
        super('Result not ready yet (202)');
        this.name = 'RetryNeededError';
    }
}

/** The 202-polling cap expired before the transfer completed. */
export class RetryTimeoutError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = 'RetryTimeoutError';
    }
}

export type RetryOn202Options = {
    signal?: AbortSignal;
    /** Message of the {@link RetryTimeoutError} thrown on cap expiry. */
    timeoutMessage?: string;
};

/** Cap on accumulated inter-poll waiting, after which 202-polling gives up. */
const RETRY_202_TIMEOUT_MS = 10 * 60 * 1_000; // 10 minutes

const DEFAULT_TIMEOUT_MESSAGE = 'Transfer did not complete before timeout';

/**
 * Polling schedule: 5 s initial wait (a just-triggered transfer needs a
 * moment), then aggressive 2 s polls for the first minute, then relax to 4 s.
 */
function retryDelayMs(attempt: number): number {
    if (attempt === 0) {
        return 5_000;
    }
    if (attempt <= 25) {
        return 2_000;
    }
    return 4_000;
}

/**
 * Retries `fetchFn` while it throws {@link RetryNeededError}; any other
 * error, including aborts, escapes immediately.
 */
export async function withRetryOn202<T>(
    fetchFn: () => Promise<T>,
    options: RetryOn202Options = {}
): Promise<T> {
    const { signal, timeoutMessage } = options;
    let waitedMs = 0;
    let attempt = 0;

    for (;;) {
        try {
            return await fetchFn();
        } catch (error) {
            if (!(error instanceof RetryNeededError)) {
                throw error;
            }
            const delay = retryDelayMs(attempt);
            if (waitedMs + delay >= RETRY_202_TIMEOUT_MS) {
                throw new RetryTimeoutError(
                    timeoutMessage ?? DEFAULT_TIMEOUT_MESSAGE
                );
            }
            await sleep(delay, signal);
            waitedMs += delay;
            attempt += 1;
        }
    }
}

/** Matches both `DOMException` aborts and plain errors named AbortError. */
export function isAbortError(error: unknown): boolean {
    return (
        (error instanceof DOMException || error instanceof Error) &&
        error.name === 'AbortError'
    );
}

/** The canonical abort sentinel; `isAbortError` matches it by name. */
export function abortError(): DOMException {
    return new DOMException('Aborted', 'AbortError');
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError());
            return;
        }
        const onAbort = (): void => {
            clearTimeout(timer);
            reject(abortError());
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}
